import { ButtonProps as HeroUiButtonProps } from '@heroui/react';
import { PressEvent } from 'react-aria-components';
import { useBoolean } from '@shared/util/hooks/useBoolean';

// helper to auto-handle async onPress functions with an internal loading state

type AsyncOnPressReturn = [boolean, HeroUiButtonProps['onPress']];

interface AsyncOnPressProps {
  onPress?: (event: PressEvent) => void | Promise<void>;
  isLoading?: boolean;
}

export function useOnPressAsync({
  onPress,
  isLoading = false,
}: AsyncOnPressProps): AsyncOnPressReturn {
  const [isWaiting, startWaiting, stopWaiting] = useBoolean(false);

  async function onPressAsync(event: PressEvent) {
    // start waiting if onPress returns a promise
    const result = onPress?.(event);
    if (result instanceof Promise) {
      try {
        startWaiting();
        await result;
      } finally {
        stopWaiting();
      }
    }
  }

  // HeroUiButton expects onPress to be synchronous, so wrap our async function
  function onPressSync(event: PressEvent) {
    // React Spectrum now stops event propagation by default. Re-enable it so
    // downstream handlers (e.g. drag-and-drop libraries) still receive the
    // event without requiring every caller to remember to call
    // `event.continuePropagation()` manually.
    // React Spectrum added `continuePropagation` on PressEvent but the
    // TypeScript types may not have caught up yet. Cast to an extended
    // interface so we can invoke it safely while keeping the compiler
    // satisfied.
    const e = event as PressEvent & { continuePropagation?: () => void };
    if (typeof e.continuePropagation === 'function') {
      e.continuePropagation();
    }

    void onPressAsync(event);
  }

  return [isWaiting || isLoading, onPressSync];
}
